import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  Stack,
  MenuItem,
  Pagination,
  FormControl,
  Select,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Edit, Delete, Print, Search } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import PrintPayment from "./PrintPayment";
import EditCollectBeautyFeesDialog from "./EditCollectBeautyFeesDialog";
import { useNavigate } from "react-router-dom";

const CollectBeautyFeesList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDialog, setOpenDialog] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalBeautyPayment, setTotalBeautyPayment] = useState(0);

  const {
    data: payments,
    refetch,
    isLoading,
    isError,
  } = useFetchObjects(
    ["procedure-payment", query, dateFilter, page, rowsPerPage],
    `procedure-payment/?page=${page}&page_size=${rowsPerPage}&search=${query}&date=${dateFilter}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("procedure-payment", token);

  useEffect(() => {
    refetch();
  }, [query, dateFilter, refetch, page, rowsPerPage]);

  useEffect(() => {
    if (payments && payments.count !== undefined) {
      setTotalBeautyPayment(payments.count);
    }
  }, [payments]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleEditClick = (payment) => {
    setCurrentPayment(payment);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPayment(null);
  };

  const handlePrintClick = (payment) => {
    setPrintData(payment);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 1:
        return "؋";
      case 2:
        return "$";
      case 3:
        return "€";
      default:
        return "";
    }
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "0.00";
    const num = parseFloat(amount);
    return num.toFixed(2);
  };

  return (
    <Grid container spacing={4} mb={4} pb={5}>
      <Grid item xs={12}>
        <Box
          padding="25px"
          backgroundColor="white"
          boxShadow={3}
          borderRadius={2}
        >
          {/* Title */}
          <Box display="flex" justifyContent="space-between" mb={4}>
            <Typography variant="h5" fontWeight={700} color="primary">
              {t("reception.procedurePaymentList")}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/reception/collect-beauty-fees/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("reception.addNew")}
            </Button>
          </Box>

          {/* Filters */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            mb={3}
            justifyContent="space-between"
          >
            <TextField
              label={t("reception.searchByProcedure")}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: <Search color="action" />,
              }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <TextField
              label={t("reception.filterByDate")}
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </Stack>

          {/* Table */}
          {isLoading ? (
            <Box textAlign="center" mt={4}>
              <CircularProgress />
              <Typography variant="body2">{t("reception.loading")}</Typography>
            </Box>
          ) : isError ? (
            <Box textAlign="center" color="red" mt={4}>
              <Typography variant="body2">
                {t("reception.errorFetchingData")}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell align="center">#</TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.patient")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.contact", "Contact")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.procedureType")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.totalAmount")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("call_center.discount")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.advancePaid")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.amountPaid")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.totalPaid")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.balanceDue")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.paymentDate")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold">
                        {t("reception.actions")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                  {payments?.results?.map((payment, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      <TableCell align="center">
                        <Typography>{payment.id}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment.display_procedure?.patient.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment?.display_procedure?.patient?.phone}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment.display_procedure?.type || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography>
                          {formatAmount(payment?.display_procedure?.total_cost)}
                          {"  "}
                          {getCurrencySymbol(
                            payment?.display_procedure?.currency
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography>
                          {payment?.display_procedure?.discount} %
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography>
                          {formatAmount(payment.advance_amount)}
                          {"  "}
                          {getCurrencySymbol(payment?.advance_currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {formatAmount(payment.amount)}
                          {"  "}
                          {getCurrencySymbol(payment?.currency)}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography>
                          {formatAmount(payment?.display_procedure?.total_paid)}
                          {"  "}
                          {getCurrencySymbol(
                            payment?.display_procedure?.currency
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {formatAmount(
                            payment?.display_procedure?.balance_due
                          )}
                          {"  "}
                          {getCurrencySymbol(
                            payment?.display_procedure?.currency
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {new Date(payment.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEditClick(payment)}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(payment.id)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                        <IconButton
                          onClick={() => handlePrintClick(payment)}
                          color="primary"
                          size="small"
                        >
                          <Print />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination Controls */}
              <Divider />
              <Grid item xs={12} m={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={4}
                  mb={2}
                >
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" mr={2}>
                      {t("reception.rowsPerPage")}:
                    </Typography>
                    <FormControl variant="standard" sx={{ minWidth: 120 }}>
                      <Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        label={t("reception.rowsPerPage")}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Pagination
                    count={Math.ceil(totalBeautyPayment / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                  />
                </Box>
              </Grid>
            </TableContainer>
          )}

          {/* Edit Dialog */}
          {currentPayment && (
            <EditCollectBeautyFeesDialog
              open={openDialog}
              onClose={handleCloseDialog}
              payment={currentPayment}
              token={token}
            />
          )}

          {/* Print Component */}
          {printData && (
            <div ref={printRef} style={{ display: "none" }}>
              <PrintPayment data={printData} />
            </div>
          )}
        </Box>
      </Grid>
      <ConfirmDialog />
    </Grid>
  );
};

export default CollectBeautyFeesList;
